import mixins from 'vue-typed-mixins';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { ErrorResponse } from '@codequality/vue-cli-base';

import { E入力分類, E不動産種類, Eフェーズ種別 } from '@/openapi/enum';
import { データApi, データRequest } from '@/openapi/user';

import C保有不動産EditRow from '@/entries/user/pages/authorized/事業所/計画書/分析/保有不動産/保有不動産EditRow/保有不動産EditRow.vue';
import AlertMixin from '@/mixins/AlertMixin';
import ElDatePickerMixin from '@/mixins/ElDatePickerMixin';
import ListMixin from '@/entries/user/mixins/ListMixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import {
  type不動産価格Keys,
  保有不動産,
  保有不動産EditItem,
  保有不動産ListItem,
} from '@/classes/service/分析/保有不動産Service';
import 法人保有不動産Service, { 法人固定価格種類Keys } from '@/classes/service/分析/法人保有不動産Service';
import 個人保有不動産Service, { 個人固定価格種類Keys } from '@/classes/service/分析/個人保有不動産Service';

const createInitialEditItem = (is法人: boolean): 保有不動産EditItem => ({
  データpk: 0,
  番号: 0,
  種類: E不動産種類.土地,
  簿価: is法人 ? {} : undefined,
  評価額: {},
});

export default mixins(AlertMixin, ElDatePickerMixin, ListMixin, RoutesMixin, 計画書Mixin).extend({
  name: 'P保有不動産Edit',

  components: {
    C保有不動産EditRow,
  },

  props: {
    is法人: {
      type: Boolean,
      default: true,
    },
  },

  data(): {
    service: 法人保有不動産Service | 個人保有不動産Service;
    type不動産価格Keys: typeof type不動産価格Keys;
    api: データApi;
    v保有不動産: 保有不動産 | null;
    newItem: 保有不動産EditItem;
    editItem: 保有不動産EditItem;
    maxNo: number;
  } {
    return {
      service: this.is法人 ? new 法人保有不動産Service() : new 個人保有不動産Service(),
      type不動産価格Keys: type不動産価格Keys,
      api: UserApiUtility.ogApi(データApi),
      v保有不動産: null,
      newItem: createInitialEditItem(this.is法人),
      editItem: createInitialEditItem(this.is法人),
      maxNo: 0,
    };
  },

  computed: {
    固定価格種類Keys(): readonly string[] {
      return this.is法人 ? 法人固定価格種類Keys : 個人固定価格種類Keys;
    },
    計算価格種類Keys(): readonly string[] {
      return this.service.get計算価格種類Keys;
    },
  },

  created(): void {
    this.$_load();
  },

  methods: {
    get不動産種類ColorCssClass(e不動産種類: E不動産種類): string {
      switch (e不動産種類) {
        case E不動産種類.土地:
          return 'blue';
        case E不動産種類.建物:
          return 'yellow';
        case E不動産種類.借地権:
          return 'green';
        default:
          return '';
      }
    },
    selectForEdit(v保有不動産: 保有不動産ListItem): void {
      const { 含み損益, 合計, ...v保有不動産EditItem } = v保有不動産;
      this.editItem = _.cloneDeep(v保有不動産EditItem);
    },
    clearForm(isNew: boolean) {
      if (isNew) {
        this.newItem = createInitialEditItem(this.is法人);
        this.$_setNextNo();
      } else {
        this.editItem = createInitialEditItem(this.is法人);
      }
    },
    async save(editItem: 保有不動産EditItem, isNew = true): Promise<void> {
      const { データpk, 番号, ...cデータ保有不動産 } = editItem;

      const request: データRequest = {
        c入力分類: this.is法人 ? E入力分類.法人保有不動産 : E入力分類.個人保有不動産,
        c番号: 番号!,
        cフェーズ種別: Eフェーズ種別.分析フェーズ,
        cデータ: JSON.stringify(cデータ保有不動産),
      };
      const response: AxiosResponse<void | ErrorResponse> = isNew
        ? await this.api.createデータ(this.計画書.pk, request)
        : await this.api.updateデータ(データpk!, request);

      if (this.isValidationErrorResponse(response)) return;
      this.$_clearAndLoad();
    },
    async remove(データpk: number): Promise<void> {
      const isCancelOrError = await this.isCancelOrValidationErrorResponseToDelete(() =>
        this.api.deleteデータ(データpk)
      );
      if (isCancelOrError) return;
      this.$_clearAndLoad();
    },

    async $_load(): Promise<void> {
      const options = {
        params: {
          sj: '保有不動産',
        },
      };
      const response = await this.api.listデータ(
        this.計画書.pk,
        this.service.getRelationalE入力分類s(),
        Eフェーズ種別.分析フェーズ,
        options
      );

      const data = response.data;
      this.maxNo = Math.max(0, ...data.map((row) => row.c番号));
      this.$_setNextNo();

      this.v保有不動産 = this.service.parse(data);
    },
    $_clearAndLoad(): void {
      this.clearForm(true);
      this.clearForm(false);
      this.$_load();
    },
    $_setNextNo(): void {
      this.newItem.番号 = this.maxNo + 1;
    },
  },
});
